import { Injectable } from "@angular/core";
import { CHDevice, DeviceClassObject } from "@app/project/model/ch-device";
import { ConfigurableProperty, DeviceClassMetadata, DeviceMetadataParser, IDeviceInfo } from "@app/project/services/device-metadata-parser";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { DeviceMetadataSelectors, DeviceSelectors } from "@app/project/store/selectors";
import { RootStoreState } from "@app/root-store";
import { PROTOCOL_ADAPTERS } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { EquatorDevicesService } from "@app/shared/services/equator-api/equator-devices";
import { ZwaveService } from "@app/shared/services/zwave-api";
import { Store, select } from "@ngrx/store";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, exhaustMap, filter, map, switchMap, take } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class DeviceAddService {
    constructor(
        private readonly store: Store<RootStoreState.State>,
        private readonly devicesDispatchers: DeviceDispatchers,
        private readonly equatorDeviceService: EquatorDevicesService,
        private readonly zwService: ZwaveService
    ) {}

    add(uuid: string, protocolAdapterName: string, template: Record<string, any> = {}) {
        const uid = `hdm:${protocolAdapterName}:${this.getRandomInt()}`;
        const templateCopy = Object.assign({ UID: uid }, template); // eslint-disable-line @typescript-eslint/naming-convention
        return this.addNoUid(uuid, protocolAdapterName, templateCopy);
    }

    addNoUid(uuid: string, protocolAdapterName: string, template: Record<string, any> = {}) {
        const children = this.extractTemplateChildren(template);

        // TODO: Add catchErrors in here
        return this.equatorDeviceService.add(uuid, protocolAdapterName, template as CHDevice).pipe(
            exhaustMap((parent: CHDevice) => {
                if (children) {
                    return this.addChildren(uuid, children, parent).pipe(map(_ => parent));
                }

                return of(parent);
            })
        );
    }

    remove(uuid: string, uid: string) {
        return this.equatorDeviceService.remove(uuid, uid);
    }

    updateDeviceProperties(uuid: string, uid: string, properties: Record<string, any>) {
        return this.equatorDeviceService.setHomeDeviceProperties(uuid, uid, properties);
    }

    private addChildren(uuid: string, children, parent) {
        const queue = [];

        children.forEach(child => {
            child["Parent.UID"] = parent.UID;
            queue.push(this.equatorDeviceService.add(uuid, parent.ProtocolAdapterName, child));
        });

        return forkJoin(queue);
    }

    private getRandomInt() {
        return Math.floor(Math.random() * 10000) + 1; // NOSONAR
    }

    private extractTemplateChildren(template) {
        let children: any[];

        if (template.children) {
            children = [...template.children];
            delete template.children;
        }

        return children;
    }

    getCameraConfigAddress(device: CHDevice) {
        const address = device?.Properties?.address;
        if (address && device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CAMERAS) {
            return { ipAddress: address, link: `http://${address}` };
        }
    }

    getNvrCameraConfigAddress(device: CHDevice) {
        const httpPort = device?.Properties?.["http.port"];
        const isCamera = device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CAMERAS;
        if (!httpPort || !isCamera) {
            console.error("Invalid device", `httpPort=${httpPort}`, `deviceType=${device?.ProtocolAdapterName}`);
            return;
        }

        // The port on Props[address] usually matches Props[http.port] but safer to be explicit
        const addressProp = device?.Properties?.address;
        if (!addressProp) {
            console.error("No device address", device?.Properties);
            return;
        }
        const [address] = addressProp.split(":");
        if (!address) {
            console.error("Invalid address", addressProp);
            return;
        }

        return `http://${address}:${httpPort}`;
    }

    getNvrConfigAddress(device: CHDevice) {
        const isCamera = device?.ProtocolAdapterName === PROTOCOL_ADAPTERS.CAMERAS;
        if (!isCamera) {
            console.error("Invalid device", device?.ProtocolAdapterName);
            return;
        }

        // Blindly assume the NVR dashboard is at the same address as camera
        // but on a different port (:80)
        const addressProp = device?.Properties?.address;
        if (!addressProp) {
            console.error("No device address", device?.Properties);
            return;
        }
        const [address] = addressProp.split(":");
        if (!address) {
            console.error("Invalid address", addressProp);
            return;
        }

        return `http://${address}`;
    }

    getInstalledDevices(protocolAdapter: string): Observable<CHDevice[]> {
        // Get parent devices
        return this.store.pipe(
            select(DeviceSelectors.getChDevicesByProtocolAdapter(protocolAdapter)),
            map((devices: CHDevice[]) => devices.filter(device => !device.ParentUID))
        );
    }

    getSortedInstallInfoFromUrl() {
        return this.devicesDispatchers.selectDeviceMetadata().pipe(
            filter(metadata => !!metadata && !!Object.values(metadata)),
            take(1),
            switchMap(_ => this.store.pipe(select(DeviceMetadataSelectors.getChDeviceAddMethodMetaDataSorted), take(1)))
        );
    }

    getChDeviceMetadataSorted() {
        return this.store.pipe(select(DeviceMetadataSelectors.getChDeviceMetaDataSorted));
    }

    selectMetadataForDeviceType(deviceType: string) {
        return this.devicesDispatchers.selectDeviceMetadata().pipe(
            filter(metadata => !!metadata && !!Object.values(metadata)),
            take(1),
            switchMap(_ =>
                this.store.pipe(
                    select(DeviceMetadataSelectors.getInstalledChDeviceMetadata),
                    take(1),
                    map(metadata => metadata[deviceType])
                )
            )
        );
    }

    hasActivatedDevices(status) {
        return Object.values(status).some(activationStatus => activationStatus === "added");
    }

    setHomeDeviceProperties(uuid, homeDeviceProperties, device: CHDevice) {
        if (homeDeviceProperties) {
            return this.equatorDeviceService.setHomeDeviceProperty(
                uuid,
                device.UID,
                Object.keys(homeDeviceProperties)[0],
                Object.values(homeDeviceProperties)[0]
            );
        }

        return of("");
    }
    setRequiredInfo(deviceInfo: IDeviceInfo) {
        let requiredInfo;
        if (deviceInfo?.configProperties) {
            requiredInfo = [];
            for (const configItem of deviceInfo.configProperties) {
                if (configItem.DeviceClass) {
                    requiredInfo = requiredInfo.concat(this.getConfigureOperations(configItem), this.getConfigureProperties(configItem));
                }
            }
        }
        return requiredInfo;
    }

    verifySuccessfulAdd(uuid: string, device: CHDevice, verificationSteps) {
        const verificationCalls = [];

        if (verificationSteps) {
            for (const step of verificationSteps) {
                if (step.type === "operation") {
                    verificationCalls.push(this.invokeDCOOperationFromMetadata(uuid, device.UID, step));
                }

                if (step.type === "contains") {
                    const property = device[step.devicePropertyPath[0]];
                    verificationCalls.push(of(property?.includes(step.value)));
                }
            }
        }

        if (!verificationCalls.length) {
            verificationCalls.push([of("")]);
        }

        return forkJoin(verificationCalls).pipe(
            map(results => results.every(result => !!result)),
            catchError(_ => of(false))
        );
    }

    invokeDCOOperationFromMetadata(uuid, uid, metadata) {
        return this.equatorDeviceService.invokeDCOOperation(uuid, uid, metadata.DeviceClass, metadata.Name, metadata.Metadata);
    }

    getConfigureProperties(configItem: DeviceClassObject | DeviceClassMetadata): ConfigurableProperty[] {
        const configurableProperties = [];

        if (configItem?.Properties && Array.isArray(configItem.Properties)) {
            configItem.Properties.forEach(property => {
                property.DeviceClass = configItem.DeviceClass;
                configurableProperties.push(property);
            });
        } else if (configItem?.Properties) {
            const allowedProperties = Object.values(this.zwService.filterBlacklistProperties(configItem));
            allowedProperties.forEach((property: any) => {
                if (property.Metadata?.access) {
                    property.DeviceClass = configItem.DeviceClass;
                    configurableProperties.push(property);
                }
            });
        }

        return configurableProperties;
    }

    getConfigureOperations(configItem: DeviceClassObject | DeviceClassMetadata): ConfigurableProperty[] {
        const configurableOperations = [];
        const paramsIdsAlreadyMatched = [];

        if (configItem?.Operations) {
            configItem.Operations.forEach(operation => {
                if (!paramsIdsAlreadyMatched.includes(operation.Metadata.IN1) && operation.Arguments) {
                    const { setOperation, getOperation } = this.extractGetAndSetOperations(operation, configurableOperations, configItem);

                    if (setOperation) {
                        setOperation.DeviceClass = configItem.DeviceClass;
                        if (getOperation) {
                            getOperation.DeviceClass = configItem.DeviceClass;
                        }
                        paramsIdsAlreadyMatched.push(setOperation.Metadata.IN1);
                        configurableOperations.push({ setOperation, getOperation });
                    }
                }
            });
        }

        return configurableOperations;
    }

    private extractGetAndSetOperations(operation: any, configurableOperations: any[], configItem: any) {
        let getOperation;
        let setOperation;

        if (operation.Name === "getParameter") {
            setOperation = this.getMatchingGetterOrSetter(configurableOperations, operation);
            getOperation = operation;
        } else if (DeviceMetadataParser.getInputs(operation).length > 0) {
            setOperation = operation;
            getOperation = this.getMatchingGetterOrSetter(configItem.Operations, operation);
        }
        return { setOperation, getOperation };
    }

    // TODO: Z-Wave Specific. See if we can move or get non-specific
    getMatchingGetterOrSetter(operations, operationToMatch) {
        const opr = operations.filter(
            operation => operation.Metadata.IN1 === operationToMatch.Metadata.IN1 && operationToMatch.Name !== operation.Name
        );
        return opr ? opr[0] : null;
    }
}
